import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Checkbox,
  FormControlLabel,
  Collapse,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircle,
  Edit,
  Delete,
  Save,
  Close,
  Add,
  FilterList,
  ExpandMore,
  Search,
  Clear,
  Download,
} from '@mui/icons-material';
import { projectsApi } from '../services/api';
import Pagination from './Pagination';

interface Requirement {
  id: string;
  title: string;
  description: string;
  requirement_type: string;
  category: string;
  zoning_districts?: string[];
  zoning_district_names?: {[code: string]: string};
  numeric_value?: string | number | null; // Can be string (ranges) or number
  unit?: string | null;
  source_document_name?: string | null;
  source_section?: string | null;
  source_page?: number;
  source_text?: string;
  confidence?: number;
  approved?: boolean;
  isCustom?: boolean;
  ordinance_type?: string | null;
  ordinance_hierarchy?: 'Local' | 'State' | 'Federal' | 'Other' | null;
}

interface RequirementsTableProps {
  projectId: string;
  onRequirementsChange?: (requirements: Requirement[]) => void;
}

const REQUIREMENT_TYPES = [
  'dimensional',
  'environmental',
  'procedural',
  'infrastructure',
  'use',
  'design',
  'parking'
];

// Display labels for requirement types
const REQUIREMENT_TYPE_LABELS: Record<string, string> = {
  'dimensional': 'Dimensional',
  'environmental': 'Environmental',
  'procedural': 'Procedural',
  'infrastructure': 'Infrastructure',
  'use': 'Use',
  'design': 'Design',
  'parking': 'Parking'
};

const getRequirementTypeLabel = (type: string): string => {
  return REQUIREMENT_TYPE_LABELS[type] || type;
};

const CATEGORIES = [
  'Setback Requirements',
  'Height Restrictions',
  'Lot Coverage',
  'Parking Requirements',
  'Landscaping',
  'Drainage',
  'Environmental Impact',
  'Permit Process',
  'Timeline Requirements',
  'Documentation',
  'Construction Standards',
  'Other'
];

export default function RequirementsTable({ projectId, onRequirementsChange }: RequirementsTableProps) {
  // Requirements data state
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    showingStart: 0,
    showingEnd: 0,
    loading: false
  });

  // Edit dialog state
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    requirement: Requirement | null;
    isNew: boolean;
  }>({ open: false, requirement: null, isNew: false });
  const [editedRequirement, setEditedRequirement] = useState<Requirement | null>(null);

  // Filter state
  const [zoningDistricts, setZoningDistricts] = useState<{code: string, display_name: string}[]>([]);
  const [selectedZoningDistricts, setSelectedZoningDistricts] = useState<string[]>(['all', 'universal']);
  const [showZoningFilter, setShowZoningFilter] = useState(false);
  const [selectedRequirementTypes, setSelectedRequirementTypes] = useState<string[]>(['all']);
  const [showRequirementTypeFilter, setShowRequirementTypeFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  // Ordinance type filtering state
  const [selectedOrdinanceHierarchies, setSelectedOrdinanceHierarchies] = useState<string[]>(['all']);
  const [selectedOrdinanceTypes, setSelectedOrdinanceTypes] = useState<string[]>(['all']);
  const [showOrdinanceFilters, setShowOrdinanceFilters] = useState(false);

  // Debounce search term - delay API calls until user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getConfidenceColor = (confidence: number): 'success' | 'warning' | 'error' => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  const getHierarchyColor = (hierarchy: string | null | undefined): 'primary' | 'success' | 'warning' | 'default' => {
    switch (hierarchy) {
      case 'Local':
        return 'primary'; // Blue
      case 'State':
        return 'success'; // Green
      case 'Federal':
        return 'warning'; // Orange
      default:
        return 'default'; // Gray (Unknown/null)
    }
  };

  // Function to fetch paginated requirements
  const fetchRequirements = async (
    page: number = paginationState.currentPage,
    limit: number = paginationState.pageSize,
    statusFilter: string = 'all',
    categoryFilter: string = 'all',
    zoningFilter?: string[],
    searchQuery: string = debouncedSearchTerm
  ) => {
    // Convert selected zoning districts to API format
    const zoneFilter = (!zoningFilter || zoningFilter.includes('all'))
      ? 'all'
      : zoningFilter.join(',');

    console.log('📡 fetchRequirements called with:', {
      page,
      limit,
      statusFilter,
      categoryFilter,
      zoneFilter,
      selectedZones: zoningFilter,
      searchQuery
    });

    setPaginationState(prev => ({ ...prev, loading: true }));
    setLoading(page === 1); // Show main loading only for first page

    try {
      console.log('🔄 Making API call to getRequirements...');

      // Convert ordinance filters to API format
      const ordinanceHierarchyFilter = selectedOrdinanceHierarchies.includes('all')
        ? 'all'
        : selectedOrdinanceHierarchies.join(',');

      const ordinanceTypeFilter = selectedOrdinanceTypes.includes('all')
        ? 'all'
        : selectedOrdinanceTypes.join(',');

      const response = await projectsApi.getRequirements(
        projectId,
        page,
        limit,
        statusFilter,
        categoryFilter, // Use passed categoryFilter directly
        'all', // Keep requirement_type_filter as 'all' since it's always 'Other'
        zoneFilter,
        searchQuery,
        ordinanceTypeFilter,
        ordinanceHierarchyFilter
      );

      console.log('📥 API response received:', {
        hasResponse: !!response,
        hasRequirements: !!response?.requirements,
        requirementsCount: response?.requirements?.length,
        isArray: Array.isArray(response?.requirements),
        pagination: response?.pagination
      });


      if (response && response.requirements && Array.isArray(response.requirements)) {
        console.log('✅ Processing', response.requirements.length, 'requirements');

        const formattedRequirements = response.requirements.map((req: any) => ({
          id: req.id,
          title: req.title,
          requirement_type: req.requirement_type || 'Other',
          category: req.category || 'General',
          description: req.description,
          numeric_value: req.measurement_value || null,
          unit: req.unit,
          source_document_name: req.source_document_name || null,
          source_section: req.source_section || null,
          source_page: req.source_page_start || 1,
          source_text: req.description,
          confidence: req.confidence_score,
          district: Array.isArray(req.zoning_districts) ? req.zoning_districts.join(', ') : '',
          zoning_districts: req.zoning_districts || [],
          zoning_district_names: req.zoning_district_names || {},
          applies_to: Array.isArray(req.applies_to) ? req.applies_to.join(', ') : '',
          measurement_method: req.measurement_method,
          tolerance: req.tolerance,
          confidence_score: req.confidence_score,
          ordinance_type: req.ordinance_type,
          ordinance_hierarchy: req.ordinance_hierarchy,
          approved: req.approved || req.validation_status === "approved" || false,
          isCustom: false
        }));

        console.log('💾 Setting', formattedRequirements.length, 'formatted requirements in state');
        setRequirements(formattedRequirements);

        // Update pagination state
        if (response.pagination) {
          console.log('📄 Updating pagination state:', response.pagination);
          setPaginationState(prev => ({
            ...prev,
            currentPage: response.pagination.current_page,
            pageSize: response.pagination.page_size,
            totalCount: response.pagination.total_count,
            totalPages: response.pagination.total_pages,
            showingStart: response.pagination.showing_start,
            showingEnd: response.pagination.showing_end,
            loading: false
          }));
        } else {
          console.warn('⚠️ No pagination data in response');
        }
      } else {
        console.error('❌ Invalid response format:', response);
        setRequirements([]);
      }
    } catch (error) {
      console.error('❌ Failed to fetch requirements:', error);
      setPaginationState(prev => ({ ...prev, loading: false }));
      setRequirements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (requirement: Requirement) => {
    setEditedRequirement({ ...requirement });
    setEditDialog({ open: true, requirement, isNew: false });
  };

  const handleAddNew = () => {
    const newRequirement: Requirement = {
      id: `custom-${Date.now()}`,
      title: '',
      description: '',
      requirement_type: 'Other',
      category: 'Other',
      approved: false,
      isCustom: true,
    };
    setEditedRequirement(newRequirement);
    setEditDialog({ open: true, requirement: newRequirement, isNew: true });
  };

  const handleSaveEdit = () => {
    if (editedRequirement) {
      if (editDialog.isNew) {
        setRequirements([...requirements, editedRequirement]);
        if (onRequirementsChange) {
          onRequirementsChange([...requirements, editedRequirement]);
        }
      } else {
        const updatedRequirements = requirements.map(req =>
          req.id === editedRequirement.id ? editedRequirement : req
        );
        setRequirements(updatedRequirements);
        if (onRequirementsChange) {
          onRequirementsChange(updatedRequirements);
        }
      }
      setEditDialog({ open: false, requirement: null, isNew: false });
      setEditedRequirement(null);
    }
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    const categoryFilter = selectedRequirementTypes.includes('all') ? 'all' : selectedRequirementTypes.join(',');
    fetchRequirements(page, paginationState.pageSize, 'all', categoryFilter, selectedZoningDistricts, debouncedSearchTerm);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPaginationState(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
    const categoryFilter = selectedRequirementTypes.includes('all') ? 'all' : selectedRequirementTypes.join(',');
    fetchRequirements(1, newPageSize, 'all', categoryFilter, selectedZoningDistricts, debouncedSearchTerm);
  };

  const handleToggleApproval = async (id: string) => {
    const requirement = requirements.find(req => req.id === id);
    if (!requirement) return;

    const newApprovalStatus = !requirement.approved;

    try {
      // Update in database via API
      await projectsApi.updateRequirementApproval(projectId, id, newApprovalStatus);

      // Update local state
      const updatedRequirements = requirements.map(req =>
        req.id === id ? { ...req, approved: newApprovalStatus } : req
      );
      setRequirements(updatedRequirements);
      if (onRequirementsChange) {
        onRequirementsChange(updatedRequirements);
      }
    } catch (error) {
      console.error('Failed to update requirement approval:', error);
      // Could add toast notification here
    }
  };

  const handleDelete = (id: string) => {
    const updatedRequirements = requirements.filter(req => req.id !== id);
    setRequirements(updatedRequirements);
    if (onRequirementsChange) {
      onRequirementsChange(updatedRequirements);
    }
  };

  // Function to load all requirements across all pages
  const loadAllRequirements = async () => {
    let allRequirements: any[] = [];
    let currentPage = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      const response = await projectsApi.getRequirements(projectId, currentPage, 100, 'all', 'all');

      if (response && response.requirements && Array.isArray(response.requirements)) {
        allRequirements = [...allRequirements, ...response.requirements];

        if (response.pagination && response.pagination.has_next) {
          currentPage++;
        } else {
          hasMorePages = false;
        }
      } else {
        hasMorePages = false;
      }
    }

    return allRequirements;
  };

  const handleToggleApproveAll = async () => {
    console.log('🔄 Starting bulk approval process...');
    console.log('Current pagination state:', paginationState);
    console.log('Current requirements count:', requirements.length);
    console.log('Selected zoning districts:', selectedZoningDistricts);
    console.log('Selected requirement types:', selectedRequirementTypes);

    try {
      // Determine the new approval status based on current page requirements
      // For simplicity, check if all visible requirements are approved
      const allApproved = requirements.length > 0 && requirements.every(req => req.approved);
      const newApprovalStatus = !allApproved;

      console.log(`🔄 ${newApprovalStatus ? 'Approving' : 'Unapproving'} requirements...`);
      console.log('Current approval status:', allApproved, '-> New status:', newApprovalStatus);

      // Use the new bulk approval API
      const zoningFilter = selectedZoningDistricts.includes('all') ? [] : selectedZoningDistricts;
      const requirementTypeFilter = selectedRequirementTypes.includes('all') ? [] : selectedRequirementTypes;

      console.log('🚀 Calling bulk approval API...');
      const result = await projectsApi.bulkUpdateRequirementApproval(
        projectId,
        newApprovalStatus,
        {
          zoning_districts: zoningFilter,
          requirement_types: requirementTypeFilter
        }
      );

      console.log('✅ Bulk approval API response:', result);
      console.log(`✅ Successfully updated ${result.updated_count} requirements`);

      console.log('🔄 Refreshing current page...');
      console.log('Refreshing page:', paginationState.currentPage, 'size:', paginationState.pageSize);

      // Refresh the current page to get updated data
      const categoryFilter = selectedRequirementTypes.includes('all') ? 'all' : selectedRequirementTypes.join(',');
      await fetchRequirements(paginationState.currentPage, paginationState.pageSize, 'all', categoryFilter, selectedZoningDistricts, debouncedSearchTerm);

      console.log('✅ Bulk approval process completed successfully');

      // Show success message
      if (result.updated_count > 0) {
        const action = newApprovalStatus ? 'approved' : 'unapproved';
        alert(`Successfully ${action} ${result.updated_count} requirements${zoningFilter.length > 0 ? ` in selected zones (${zoningFilter.join(', ')})` : ''}.`);
      } else {
        alert('No requirements found to update with the current filters.');
      }

    } catch (error) {
      console.error('❌ Failed to update requirements approval:', error);
      alert('Failed to update requirements approval. Please try again.');
    }
  };

  const handleExportToCSV = async () => {
    try {
      console.log('📊 Starting CSV export for project:', projectId);

      // Call API to get CSV blob
      const blob = await projectsApi.exportApprovedRequirementsCSV(projectId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0].replace(/-/g, '');
      link.download = `approved_requirements_${currentDate}.csv`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ CSV export completed successfully');
    } catch (error: any) {
      console.error('❌ Failed to export CSV:', error);

      // Check if error response contains detail message
      if (error.response?.status === 400) {
        alert('No approved requirements found. Please approve requirements before exporting.');
      } else {
        alert('Failed to export CSV. Please try again.');
      }
    }
  };

  // Load initial data when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      const categoryFilter = selectedRequirementTypes.includes('all') ? 'all' : selectedRequirementTypes.join(',');
      await fetchRequirements(1, 20, 'all', categoryFilter, selectedZoningDistricts, debouncedSearchTerm);
    };
    loadInitialData();
  }, [projectId]);

  // Reload data when zoning districts change
  useEffect(() => {
    if (requirements.length > 0) { // Only reload if we have already loaded data initially
      const categoryFilter = selectedRequirementTypes.includes('all') ? 'all' : selectedRequirementTypes.join(',');
      fetchRequirements(1, paginationState.pageSize, 'all', categoryFilter, selectedZoningDistricts, debouncedSearchTerm);
    }
  }, [selectedZoningDistricts]);

  // Reload data when requirement types change
  useEffect(() => {
    if (requirements.length > 0) { // Only reload if we have already loaded data initially
      const categoryFilter = selectedRequirementTypes.includes('all') ? 'all' : selectedRequirementTypes.join(',');
      fetchRequirements(1, paginationState.pageSize, 'all', categoryFilter, selectedZoningDistricts, debouncedSearchTerm);
    }
  }, [selectedRequirementTypes]);

  // Reload data when search term changes (debounced)
  useEffect(() => {
    if (requirements.length > 0) { // Only search if we have initially loaded data
      const categoryFilter = selectedRequirementTypes.includes('all') ? 'all' : selectedRequirementTypes.join(',');
      fetchRequirements(1, paginationState.pageSize, 'all', categoryFilter, selectedZoningDistricts, debouncedSearchTerm);
    }
  }, [debouncedSearchTerm]);

  // Reload data when ordinance hierarchy filter changes
  useEffect(() => {
    if (requirements.length > 0) {
      const categoryFilter = selectedRequirementTypes.includes('all') ? 'all' : selectedRequirementTypes.join(',');
      fetchRequirements(1, paginationState.pageSize, 'all', categoryFilter, selectedZoningDistricts, debouncedSearchTerm);
    }
  }, [selectedOrdinanceHierarchies]);

  // Reload data when ordinance type filter changes
  useEffect(() => {
    if (requirements.length > 0) {
      const categoryFilter = selectedRequirementTypes.includes('all') ? 'all' : selectedRequirementTypes.join(',');
      fetchRequirements(1, paginationState.pageSize, 'all', categoryFilter, selectedZoningDistricts, debouncedSearchTerm);
    }
  }, [selectedOrdinanceTypes]);

  // Fetch zoning districts when component mounts (not dependent on requirements)
  useEffect(() => {
    const fetchZoningDistricts = async () => {
      try {
        const response = await projectsApi.getZoningDistricts(projectId);
        setZoningDistricts(response.zoning_districts || []);
      } catch (error) {
        console.error('Error fetching zoning districts:', error);
        setZoningDistricts([]);
      }
    };

    fetchZoningDistricts();
  }, [projectId]); // Only depend on projectId, not requirements.length

  // Server-side filtering is now implemented - requirements are already filtered by backend
  const filteredRequirements = requirements;
  const approvedCount = requirements.filter(req => req.approved).length;
  const allApproved = requirements.length > 0 && requirements.every(req => req.approved);

  // Check if search is active
  const isSearchActive = debouncedSearchTerm.trim().length > 0;

  // Check if filters are active
  const isZoneFilterActive = !(selectedZoningDistricts.includes('all') && selectedZoningDistricts.includes('universal') && selectedZoningDistricts.length === 2);
  const isRequirementTypeFilterActive = !selectedRequirementTypes.includes('all');

  // Helper functions for zone selection
  const handleZoneToggle = (zoneCode: string) => {
    if (zoneCode === 'all') {
      // Toggle "all" - if currently selected, switch to just universal, if not selected, select all + universal
      if (selectedZoningDistricts.includes('all')) {
        // If all is selected, switch to just universal
        setSelectedZoningDistricts(['universal']);
      } else {
        // Add "all" back to the selection (along with other selected zones)
        setSelectedZoningDistricts(prev => ['all', ...prev.filter(z => z !== 'all')]);
      }
    } else if (zoneCode === 'universal') {
      // Toggle "universal" - if currently selected, remove it, if not selected, add it
      if (selectedZoningDistricts.includes('universal')) {
        const newSelection = selectedZoningDistricts.filter(zone => zone !== 'universal');
        // If removing universal leaves us with nothing, default to "all"
        setSelectedZoningDistricts(newSelection.length === 0 ? ['all'] : newSelection);
      } else {
        // Add universal to current selection
        setSelectedZoningDistricts(prev => [...prev, 'universal']);
      }
    } else {
      // Regular zone selection
      setSelectedZoningDistricts(prev => {
        const hasAll = prev.includes('all');
        const hasUniversal = prev.includes('universal');

        if (hasAll && hasUniversal && prev.length === 2) {
          // If only "all" and "universal" are selected, replace with just this zone
          return [zoneCode];
        } else {
          if (prev.includes(zoneCode)) {
            // Remove this zone
            const newSelection = prev.filter(zone => zone !== zoneCode);
            // If no zones selected, default to "all" + "universal"
            return newSelection.length === 0 ? ['all', 'universal'] : newSelection;
          } else {
            // Add this zone (remove "all" if it was selected)
            return [...prev.filter(zone => zone !== 'all'), zoneCode];
          }
        }
      });
    }
  };

  const handleRemoveZone = (zoneCode: string) => {
    if (zoneCode === 'all') return; // Can't remove "all" from chip display

    setSelectedZoningDistricts(prev => {
      const newSelection = prev.filter(zone => zone !== zoneCode);
      // If removing a zone leaves us with nothing, default to "all" + "universal"
      return newSelection.length === 0 ? ['all', 'universal'] : newSelection;
    });
  };

  // Helper functions for requirement type selection
  const handleRequirementTypeToggle = (requirementType: string) => {
    if (requirementType === 'all') {
      // Toggle "all" - if currently selected, switch to empty, if not selected, select all
      if (selectedRequirementTypes.includes('all')) {
        setSelectedRequirementTypes([]);
      } else {
        setSelectedRequirementTypes(['all']);
      }
    } else {
      // Regular requirement type selection
      setSelectedRequirementTypes(prev => {
        if (prev.includes('all')) {
          // If "all" is selected, replace with just this type
          return [requirementType];
        } else {
          if (prev.includes(requirementType)) {
            // Remove this type
            const newSelection = prev.filter(type => type !== requirementType);
            // If no types selected, default to "all"
            return newSelection.length === 0 ? ['all'] : newSelection;
          } else {
            // Add this type
            return [...prev, requirementType];
          }
        }
      });
    }
  };

  const handleRemoveRequirementType = (requirementType: string) => {
    if (requirementType === 'all') return; // Can't remove "all" from chip display

    setSelectedRequirementTypes(prev => {
      const newSelection = prev.filter(type => type !== requirementType);
      // If removing a type leaves us with nothing, default to "all"
      return newSelection.length === 0 ? ['all'] : newSelection;
    });
  };

  // Helper functions for ordinance hierarchy filtering
  const handleOrdinanceHierarchyChange = (hierarchy: string, checked: boolean) => {
    let newSelected = [...selectedOrdinanceHierarchies].filter(h => h !== 'all');

    if (checked) {
      newSelected.push(hierarchy);
    } else {
      newSelected = newSelected.filter(h => h !== hierarchy);
    }

    setSelectedOrdinanceHierarchies(newSelected.length === 0 ? ['all'] : newSelected);
  };

  const handleRemoveOrdinanceHierarchy = (hierarchy: string) => {
    if (hierarchy === 'all') return;

    setSelectedOrdinanceHierarchies(prev => {
      const newSelection = prev.filter(h => h !== hierarchy);
      return newSelection.length === 0 ? ['all'] : newSelection;
    });
  };

  // Helper function to highlight matching text
  const highlightText = (text: string, searchTerm: string): React.ReactNode => {
    if (!searchTerm.trim()) {
      return text;
    }

    // Create a simple regex for highlighting (case-insensitive)
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === searchTerm.toLowerCase()) {
        return (
          <span key={index} style={{ backgroundColor: '#ffeb3b', fontWeight: 600 }}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <Box>
      {/* Inline Controls */}
      <Box sx={{ mb: 3 }}>
        {/* Top Row: Selected Zone Tags and Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          {/* Left: Filter Controls and Tags */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
            {/* Requirement Type Filter Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              endIcon={<ExpandMore sx={{ transform: showRequirementTypeFilter ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />}
              onClick={() => setShowRequirementTypeFilter(!showRequirementTypeFilter)}
              sx={{ minWidth: 140 }}
            >
              Requirement Types
            </Button>

            {/* Zoning District Filter Button */}
            {zoningDistricts.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterList />}
                endIcon={<ExpandMore sx={{ transform: showZoningFilter ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />}
                onClick={() => setShowZoningFilter(!showZoningFilter)}
                sx={{ minWidth: 120 }}
              >
                Zones
              </Button>
            )}

            {/* Ordinance Type Filter Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              endIcon={<ExpandMore sx={{ transform: showOrdinanceFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />}
              onClick={() => setShowOrdinanceFilters(!showOrdinanceFilters)}
              sx={{ minWidth: 150 }}
            >
              Ordinance Type
            </Button>

            {/* Selected Requirement Type Tags */}
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {selectedRequirementTypes.includes('all') ? (
                <Chip label="All Types" size="small" color="secondary" />
              ) : (
                selectedRequirementTypes.map(requirementType => (
                  <Chip
                    key={requirementType}
                    label={getRequirementTypeLabel(requirementType)}
                    size="small"
                    color="secondary"
                    variant="filled"
                    onDelete={() => handleRemoveRequirementType(requirementType)}
                  />
                ))
              )}
            </Stack>

            {/* Selected Zone Tags */}
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {selectedZoningDistricts.includes('all') && selectedZoningDistricts.includes('universal') && selectedZoningDistricts.length === 2 ? (
                <>
                  <Chip label="All Districts" size="small" color="primary" />
                  <Chip label="Universal Zone" size="small" color="success" variant="outlined" />
                </>
              ) : selectedZoningDistricts.includes('all') && selectedZoningDistricts.length === 1 ? (
                <Chip label="All Districts Only" size="small" color="primary" />
              ) : (
                selectedZoningDistricts.map(zoneCode => {
                  if (zoneCode === 'universal') {
                    return (
                      <Chip
                        key={zoneCode}
                        label="Universal Zone"
                        size="small"
                        color="success"
                        variant="filled"
                        onDelete={() => handleRemoveZone(zoneCode)}
                        icon={<span>🌍</span>}
                      />
                    );
                  }
                  const district = zoningDistricts.find(d => d.code === zoneCode);
                  return (
                    <Chip
                      key={zoneCode}
                      label={district?.display_name || zoneCode}
                      size="small"
                      color="primary"
                      variant="filled"
                      onDelete={() => handleRemoveZone(zoneCode)}
                    />
                  );
                })
              )}
            </Stack>

            {/* Selected Ordinance Hierarchy Tags */}
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {selectedOrdinanceHierarchies.includes('all') ? (
                <Chip label="All Hierarchies" size="small" color="info" variant="outlined" />
              ) : (
                selectedOrdinanceHierarchies.map(hierarchy => (
                  <Chip
                    key={hierarchy}
                    label={hierarchy === 'null' ? 'Unknown' : hierarchy}
                    size="small"
                    color={getHierarchyColor(hierarchy === 'null' ? null : hierarchy)}
                    variant="filled"
                    onDelete={() => handleRemoveOrdinanceHierarchy(hierarchy)}
                  />
                ))
              )}
            </Stack>

            {/* Summary */}
            <Typography variant="body2" color="text.secondary">
              {paginationState.totalCount > 0 ? (
                <>
                  {isSearchActive || isRequirementTypeFilterActive || isZoneFilterActive ? (
                    <>
                      <span style={{ fontWeight: 600, color: '#1976d2' }}>
                        🔍 Filtered Results:
                      </span>
                      {` Showing ${paginationState.showingStart}-${paginationState.showingEnd} of ${paginationState.totalCount} Requirements`}
                      {isSearchActive && ` matching "${debouncedSearchTerm}"`}
                      {isRequirementTypeFilterActive && ` • Types: ${selectedRequirementTypes.map(type => getRequirementTypeLabel(type)).join(', ')}`}
                      {isZoneFilterActive && ` • Zones: ${selectedZoningDistricts.filter(z => z !== 'all').join(', ')}`}
                      {` • ${approvedCount}/${filteredRequirements.length} Approved (this page)`}
                    </>
                  ) : (
                    `Showing ${paginationState.showingStart}-${paginationState.showingEnd} of ${paginationState.totalCount} Requirements (All Types + All Zones) • ${approvedCount}/${filteredRequirements.length} Approved (this page)`
                  )}
                </>
              ) : (
                `${filteredRequirements.length} Requirements • ${approvedCount}/${filteredRequirements.length} Approved`
              )}
            </Typography>
          </Box>

          {/* Right: Page Size Selector and Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Show:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={paginationState.pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  disabled={paginationState.loading || loading}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                per page
              </Typography>
            </Box>

            <Button
              variant={allApproved ? "contained" : "outlined"}
              size="small"
              onClick={handleToggleApproveAll}
              color="success"
              title={
                selectedZoningDistricts.includes('all')
                  ? `${allApproved ? 'Unapprove' : 'Approve'} ALL ${paginationState.totalCount} requirements across all pages`
                  : `${allApproved ? 'Unapprove' : 'Approve'} requirements in selected zones (${selectedZoningDistricts.map(code =>
                      zoningDistricts.find(d => d.code === code)?.display_name || code
                    ).join(', ')}) across all pages`
              }
              sx={{
                fontWeight: 600,
                ...(selectedZoningDistricts.includes('all') ? {} : {
                  background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)',
                  }
                })
              }}
            >
              {selectedZoningDistricts.includes('all') ? (
                allApproved ? `Unapprove All (${paginationState.totalCount})` : `Approve All (${paginationState.totalCount})`
              ) : (
                <>
                  {allApproved ? 'Unapprove' : 'Approve'}{' '}
                  <span style={{ textDecoration: 'underline' }}>
                    {selectedZoningDistricts.length > 1 ? 'Zones' : 'Zone'}
                  </span>
                  {' '}({paginationState.totalCount})
                </>
              )}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={handleAddNew}
              color="secondary"
            >
              Add Custom
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Download />}
              onClick={handleExportToCSV}
              color="primary"
              title="Export all approved requirements to CSV with terminology"
              sx={{ fontWeight: 600 }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        {/* Requirement Type Selection Checkboxes */}
        <Collapse in={showRequirementTypeFilter}>
          <Paper sx={{ p: 2, bgcolor: 'grey.50', border: 1, borderColor: 'grey.300', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Select Requirement Types:
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1,
              alignItems: 'start'
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedRequirementTypes.includes('all')}
                    onChange={() => handleRequirementTypeToggle('all')}
                    size="small"
                  />
                }
                label="All Types"
                sx={{ m: 0 }}
              />
              {REQUIREMENT_TYPES.map(requirementType => (
                <FormControlLabel
                  key={requirementType}
                  control={
                    <Checkbox
                      checked={selectedRequirementTypes.includes(requirementType)}
                      onChange={() => handleRequirementTypeToggle(requirementType)}
                      size="small"
                    />
                  }
                  label={getRequirementTypeLabel(requirementType)}
                  sx={{ m: 0 }}
                />
              ))}
            </Box>
          </Paper>
        </Collapse>

        {/* Zone Selection Checkboxes */}
        {zoningDistricts.length > 0 && (
          <Collapse in={showZoningFilter}>
            <Paper sx={{ p: 2, bgcolor: 'grey.50', border: 1, borderColor: 'grey.300' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Select Zoning Districts for Your Project:
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 1,
                alignItems: 'start'
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedZoningDistricts.includes('all')}
                      onChange={() => handleZoneToggle('all')}
                      size="small"
                    />
                  }
                  label="All Districts"
                  sx={{ m: 0 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedZoningDistricts.includes('universal')}
                      onChange={() => handleZoneToggle('universal')}
                      size="small"
                      color="success"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      🌍 Universal Zone
                    </Box>
                  }
                  sx={{ m: 0, fontWeight: 500 }}
                />
                {zoningDistricts.filter(d => d.code !== 'universal').map(district => (
                  <FormControlLabel
                    key={district.code}
                    control={
                      <Checkbox
                        checked={selectedZoningDistricts.includes(district.code)}
                        onChange={() => handleZoneToggle(district.code)}
                        size="small"
                      />
                    }
                    label={district.display_name}
                    sx={{ m: 0 }}
                  />
                ))}
              </Box>
            </Paper>
          </Collapse>
        )}

        {/* Ordinance Type Filters */}
        <Collapse in={showOrdinanceFilters}>
          <Paper sx={{ p: 2, bgcolor: 'grey.50', border: 1, borderColor: 'grey.300' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Filter by Ordinance Hierarchy:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedOrdinanceHierarchies.includes('all')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedOrdinanceHierarchies(['all']);
                      } else {
                        setSelectedOrdinanceHierarchies([]);
                      }
                    }}
                    size="small"
                  />
                }
                label="All Hierarchies"
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedOrdinanceHierarchies.includes('Local')}
                    onChange={(e) => handleOrdinanceHierarchyChange('Local', e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip label="Local" size="small" color="primary" />
                    <Typography variant="body2" color="text.secondary">Municipal/County</Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedOrdinanceHierarchies.includes('State')}
                    onChange={(e) => handleOrdinanceHierarchyChange('State', e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip label="State" size="small" color="success" />
                    <Typography variant="body2" color="text.secondary">State Codes</Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedOrdinanceHierarchies.includes('Federal')}
                    onChange={(e) => handleOrdinanceHierarchyChange('Federal', e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip label="Federal" size="small" color="warning" />
                    <Typography variant="body2" color="text.secondary">Federal Law</Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedOrdinanceHierarchies.includes('null')}
                    onChange={(e) => handleOrdinanceHierarchyChange('null', e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip label="Unknown" size="small" color="default" />
                    <Typography variant="body2" color="text.secondary">Unclassified</Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
            </Box>
          </Paper>
        </Collapse>

        {/* Filter Active Indicators */}
        {(isZoneFilterActive || isRequirementTypeFilterActive || isSearchActive) && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              bgcolor: '#e3f2fd',
              border: 1,
              borderColor: '#1976d2',
              '& .MuiAlert-message': {
                fontWeight: 500
              }
            }}
            icon={<FilterList />}
          >
            <Typography variant="body2">
              <strong>🔍 Active Filters:</strong>
              {isSearchActive && (
                <> Search: "<strong>{debouncedSearchTerm}</strong>"</>
              )}
              {isRequirementTypeFilterActive && (
                <>
                  {isSearchActive && ' + '}
                  Types: <strong>{selectedRequirementTypes.map(type => getRequirementTypeLabel(type)).join(', ')}</strong>
                </>
              )}
              {isZoneFilterActive && (
                <>
                  {(isSearchActive || isRequirementTypeFilterActive) && ' + '}
                  Zones: <strong>
                    {selectedZoningDistricts.map(code => {
                      if (code === 'universal') return 'Universal Zone';
                      if (code === 'all') return 'All Districts';
                      return zoningDistricts.find(d => d.code === code)?.display_name || code;
                    }).join(', ')}
                  </strong>
                </>
              )}
              {paginationState.totalCount > 0 && (
                <> • Found <strong>{paginationState.totalCount}</strong> matching requirements</>
              )}
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search requirements by title or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white',
            }
          }}
        />
      </Box>

      {filteredRequirements.length === 0 && paginationState.totalCount === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {debouncedSearchTerm.trim()
            ? `No requirements match "${debouncedSearchTerm}". Try a different search term or clear the search.`
            : 'No requirements found for the selected filters. Try selecting "All Districts" or adjusting your filters.'
          }
        </Alert>
      ) : requirements.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No requirements found. Start extraction to populate requirements, or add custom requirements manually.
        </Alert>
      ) : (
        <Paper elevation={0} sx={{ border: 1, borderColor: 'grey.300' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Requirement</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Ordinance Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Value/Unit</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequirements.map((req) => (
                  <TableRow
                    key={req.id}
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                        cursor: req.source_text ? 'help' : 'default'
                      }
                    }}
                  >
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Tooltip
                        title={
                          req.source_text ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                Source Text:
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                "{req.source_text}"
                              </Typography>
                              {req.confidence && (
                                <Typography variant="caption" color="text.secondary">
                                  Confidence: {(req.confidence * 100).toFixed(0)}%
                                </Typography>
                              )}
                            </Box>
                          ) : req.description || req.title
                        }
                        arrow
                        placement="top"
                        enterDelay={500}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {highlightText(req.title, searchTerm)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                            {highlightText(req.description, searchTerm)}
                          </Typography>
                          {req.isCustom && (
                            <Chip label="Custom" size="small" color="info" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{req.category}</Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          label={req.ordinance_hierarchy || 'Unknown'}
                          size="small"
                          color={getHierarchyColor(req.ordinance_hierarchy)}
                          sx={{ fontWeight: 600 }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.7rem'
                          }}
                        >
                          {req.ordinance_type || 'Unclassified'}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      {(req.numeric_value !== null && req.numeric_value !== undefined && req.numeric_value !== '') && req.unit ? (
                        <Chip
                          label={`${req.numeric_value} ${req.unit}`}
                          size="small"
                          color="success"
                          variant="filled"
                        />
                      ) : req.numeric_value !== null && req.numeric_value !== undefined && req.numeric_value !== '' ? (
                        <Chip
                          label={`${req.numeric_value}`}
                          size="small"
                          color="success"
                          variant="filled"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {req.source_document_name ? (
                        <Box>
                          <Typography variant="body2">
                            {req.source_document_name}
                          </Typography>
                          {req.source_section && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {req.source_section}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {req.isCustom ? 'Manual Entry' : 'N/A'}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {req.confidence ? (
                        <Chip
                          label={`${(req.confidence * 100).toFixed(0)}%`}
                          size="small"
                          color={getConfidenceColor(req.confidence)}
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {req.isCustom ? 'N/A' : 'Unknown'}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={req.approved ? 'Approved' : 'Pending'}
                        size="small"
                        color={req.approved ? 'success' : 'warning'}
                        variant={req.approved ? 'filled' : 'outlined'}
                      />
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit Requirement">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(req)}
                            color="primary"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={req.approved ? "Unapprove Requirement" : "Approve Requirement"}>
                          <IconButton
                            size="small"
                            color={req.approved ? "success" : "default"}
                            onClick={() => handleToggleApproval(req.id)}
                          >
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete Requirement">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(req.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Pagination */}
      {paginationState.totalCount > 0 && (
        <Pagination
          currentPage={paginationState.currentPage}
          totalPages={paginationState.totalPages}
          onPageChange={handlePageChange}
          loading={paginationState.loading}
          showingStart={paginationState.showingStart}
          showingEnd={paginationState.showingEnd}
          totalCount={paginationState.totalCount}
          itemName="requirements"
        />
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, requirement: null, isNew: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editDialog.isNew ? 'Add New Requirement' : 'Edit Requirement'}
        </DialogTitle>
        <DialogContent>
          {editedRequirement && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Title"
                value={editedRequirement.title}
                onChange={(e) => setEditedRequirement({
                  ...editedRequirement,
                  title: e.target.value
                })}
                fullWidth
                required
              />

              <TextField
                label="Description"
                multiline
                rows={3}
                value={editedRequirement.description}
                onChange={(e) => setEditedRequirement({
                  ...editedRequirement,
                  description: e.target.value
                })}
                fullWidth
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Requirement Type</InputLabel>
                  <Select
                    value={editedRequirement.requirement_type}
                    onChange={(e) => setEditedRequirement({
                      ...editedRequirement,
                      requirement_type: e.target.value
                    })}
                    label="Requirement Type"
                  >
                    {REQUIREMENT_TYPES.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={editedRequirement.category}
                    onChange={(e) => setEditedRequirement({
                      ...editedRequirement,
                      category: e.target.value
                    })}
                    label="Category"
                  >
                    {CATEGORIES.map(category => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Numeric Value"
                  type="number"
                  value={editedRequirement.numeric_value || ''}
                  onChange={(e) => setEditedRequirement({
                    ...editedRequirement,
                    numeric_value: e.target.value ? parseFloat(e.target.value) : null
                  })}
                  sx={{ flex: 1 }}
                />

                <TextField
                  label="Unit"
                  value={editedRequirement.unit || ''}
                  onChange={(e) => setEditedRequirement({
                    ...editedRequirement,
                    unit: e.target.value || null
                  })}
                  placeholder="ft, sq ft, etc."
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditDialog({ open: false, requirement: null, isNew: false })}
            startIcon={<Close />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            startIcon={<Save />}
            disabled={!editedRequirement?.title.trim()}
          >
            {editDialog.isNew ? 'Add Requirement' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}